import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');

    if (!closestSection) return;

    closestSection.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');

    if (!closestSection) return;

    closestSection.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );
  }

  /**
   * Sincroniza el precio recién renderizado por Horizon
   * con la cabecera móvil personalizada de SHELUET.
   *
   * Solo actúa si existe [data-sheluet-mobile-price].
   * No modifica tarjetas, carrito, Bundle Offers ni recomendaciones.
   *
   * @param {HTMLElement} newPrice
   */
  #syncSheluetMobilePrice(newPrice) {
    if (!window.matchMedia('(max-width: 749px)').matches) {
      return;
    }

    const productComponent = this.closest('product-component');

    if (!productComponent) {
      return;
    }

    const mobilePriceTarget =
      productComponent.querySelector(
        '[data-sheluet-mobile-price]'
      );

    if (!mobilePriceTarget) {
      return;
    }

    /*
     * Copiamos exactamente el markup que Horizon
     * acaba de generar para la variante seleccionada.
     *
     * Así se conservan:
     * - variant.price
     * - variant.compare_at_price
     * - la lógica que oculta compare_at_price
     *   cuando no es mayor que price
     * - el formato de moneda del tema
     */
    mobilePriceTarget.innerHTML =
      newPrice.innerHTML;
  }

  /**
   * Handles product select event and updates the price.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element)
      ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) {
          return;
        }

        const {
          html,
          newProduct
        } = detail;

        if (newProduct) {
          this.dataset.productId =
            newProduct.id;
        } else if (
          detail.productId
          &&
          detail.productId !==
            this.dataset.productId
        ) {
          return;
        }

        const {
          priceContainer,
          volumePricingNote
        } = this.refs;

        /*
         * Buscamos el mismo bloque product-price
         * en el HTML actualizado que devuelve Horizon.
         */
        const newProductPrice =
          html.querySelector(
            `product-price[data-block-id="${this.dataset.blockId}"]`
          );

        if (!newProductPrice) {
          return;
        }

        /*
         * PRECIO
         */
        const newPrice =
          newProductPrice.querySelector(
            '[ref="priceContainer"]'
          );

        if (
          newPrice
          &&
          priceContainer
        ) {
          /*
           * Comportamiento nativo de Horizon:
           * sustituye el precio por el de la
           * nueva variante.
           */
          priceContainer.replaceWith(
            newPrice
          );

          /*
           * SHELUET:
           * sincronizamos también la cabecera móvil
           * que aparece encima de la galería.
           */
          this.#syncSheluetMobilePrice(
            newPrice
          );
        }

        /*
         * VOLUME PRICING NOTE
         * Se mantiene exactamente la lógica
         * original de Horizon.
         */
        const newNote =
          newProductPrice.querySelector(
            '[ref="volumePricingNote"]'
          );

        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          /*
           * Usamos newPrice porque priceContainer
           * ya apunta al elemento separado del DOM.
           */
          newPrice?.insertAdjacentElement(
            'afterend',
            /** @type {Element} */ (
              newNote.cloneNode(true)
            )
          );
        } else {
          volumePricingNote.replaceWith(
            newNote
          );
        }

        /*
         * PAYMENT INSTALLMENTS
         * Conservamos exactamente el comportamiento
         * original de Horizon.
         */
        const installmentsInput =
          /** @type {HTMLInputElement|null} */ (
            this.querySelector(
              `#product-form-installment-${this.dataset.blockId} input[name="id"]`
            )
          );

        if (installmentsInput) {
          installmentsInput.value =
            detail.resource?.id ?? '';

          installmentsInput.dispatchEvent(
            new Event(
              'change',
              {
                bubbles: true
              }
            )
          );
        }
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[product-price] Event promise rejected:',
            error
          );
        }
      });
  };
}

if (
  !customElements.get(
    'product-price'
  )
) {
  customElements.define(
    'product-price',
    ProductPrice
  );
}